import React, { useState, useEffect, useMemo } from 'react';
import { usePermissionsContext } from '../../contexts/PermissionsContext';
import { getCompanyLogoUrl } from '../../utils/imageUtils';
import {
  Users,
  Building2,
  Calendar,
  CalendarX,
  Clock,
  DollarSign,
  Gift,
  Umbrella,
  FileText,
  UserCog,
  LogOut,
  Menu,
  X,
  ChefHat,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  CreditCard,
  FileSpreadsheet,
  Shield,
  TrendingUp,
  MinusCircle,
  Calculator,
  GitCompare,
  BarChart3,
  Briefcase,
  Home,
  CheckSquare,
  Settings,
  Network,
  Layers,
  Landmark,
  Package,
  FileBarChart2,
  ClipboardList,
  Award,
  Key
} from 'lucide-react';

// Convierte el array plano de submenu (con isGroup) en grupos colapsables
function parseGroups(submenu) {
  const result = [];
  let current = null;
  for (const item of submenu) {
    if (item.isGroup) {
      current = { id: item.id, label: item.label, items: [] };
      result.push(current);
    } else if (current) {
      current.items.push(item);
    }
  }
  return result;
}

export default function Sidebar({ currentModule, setCurrentModule, onLogout, currentUser, currentCompany, pendientesCount = 0 }) {
  const { can } = usePermissionsContext();
  const [isOpen, setIsOpen] = useState(true);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState([]);

  // Estado de grupos colapsables con persistencia en localStorage
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebar_expanded_groups') || '[]'); }
    catch { return []; }
  });

  // Módulos nuevos ya vistos — persiste entre sesiones
  const [seenNewModules, setSeenNewModules] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('seen_new_modules') || '[]')); }
    catch { return new Set(); }
  });

  const isNewBadgeVisible = (id) => !seenNewModules.has(id);

  const markAsSeen = (id) => {
    setSeenNewModules(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      localStorage.setItem('seen_new_modules', JSON.stringify([...next]));
      return next;
    });
  };

  // Persistir cambios
  useEffect(() => {
    localStorage.setItem('sidebar_expanded_groups', JSON.stringify(expandedGroups));
  }, [expandedGroups]);

  // Mapa módulo → grupo (calculado una vez desde el submenu estático)
  const moduleToGroup = useMemo(() => {
    const map = {};
    // Solo aplica al submenu de nómina (estructura estática)
    const payrollSubmenu = [
      { isGroup: true, id: 'grp-proc' },
      'payroll-main', 'variable-incomes', 'other-deductions', 'prestamos', 'provisions',
      { isGroup: true, id: 'grp-imp' },
      'social-security', 'tss-planillas', 'isr', 'ir13', 'creditos-isr', 'tax-benefits',
      { isGroup: true, id: 'grp-anal' },
      'compare-payrolls', 'payroll-statistics', 'payroll-reports',
      { isGroup: true, id: 'grp-cfg' },
      'payroll-config',
      { isGroup: true, id: 'grp-especiales' },
      'bonificaciones',
      'regalia-pascual',
    ];
    let currentGroupId = null;
    for (const entry of payrollSubmenu) {
      if (typeof entry === 'object') { currentGroupId = entry.id; }
      else if (currentGroupId) { map[entry] = currentGroupId; }
    }
    return map;
  }, []);

  // Auto-apertura del grupo cuando cambia el módulo activo
  useEffect(() => {
    const groupId = moduleToGroup[currentModule];
    if (groupId && !expandedGroups.includes(groupId)) {
      setExpandedGroups([groupId]);
      // También expandir el menú padre (Nómina) si no está abierto
      setExpandedMenus(prev => prev.includes('payroll') ? prev : [...prev, 'payroll']);
    }
  }, [currentModule]); // eslint-disable-line

  const menuCategories = [
    {
      name: 'Principal',
      items: [
        {
          id: 'dashboard',
          label: 'Dashboard',
          icon: Home,
          iconColor: 'text-blue-500',
          description: 'Vista general del sistema'
        },
        {
          id: 'hr-management',
          label: 'Gestión de Personal',
          icon: Users,
          iconColor: 'text-violet-500',
          description: 'Administración de RRHH',
          hasSubmenu: true,
          submenu: [
            { id: 'employees',         label: 'Empleados',           icon: Users,          iconColor: 'text-violet-400', permission: 'EMPLOYEE_VIEW'          },
            { id: 'related-persons',   label: 'Personas Relacionadas', icon: Shield,       iconColor: 'text-sky-400',    permission: 'EMPLOYEE_EDIT'          },
            { id: 'contracts',         label: 'Contratos',           icon: FileText,       iconColor: 'text-blue-400',   permission: 'CONTRACT_VIEW'          },
            { id: 'personnel-actions', label: 'Acciones de Personal',icon: TrendingUp,     iconColor: 'text-emerald-400',permission: 'PERSONAL_ACTION_VIEW'   },
            { id: 'documents',         label: 'Documentos',          icon: FileSpreadsheet,iconColor: 'text-amber-400',  permission: 'DOCUMENT_UPLOAD'        },
            { id: 'history',           label: 'Historial',           icon: BarChart3,      iconColor: 'text-gray-400',   permission: 'EMPLOYEE_EDIT'          }
          ]
        },
        {
          id: 'org-structure',
          label: 'Estructura Organizacional',
          icon: Network,
          iconColor: 'text-blue-500',
          description: 'Estructura de la empresa',
          hasSubmenu: true,
          submenu: [
            { id: 'org-chart',       label: 'Organigrama',   icon: Network,   iconColor: 'text-blue-400',   permission: 'EMPLOYEE_VIEW'  },
            { id: 'org-units',       label: 'Unidades',      icon: Layers,    iconColor: 'text-indigo-400', permission: 'EMPLOYEE_EDIT'  },
            { id: 'org-departments', label: 'Departamentos', icon: Building2, iconColor: 'text-blue-400',   permission: 'EMPLOYEE_EDIT'  },
            { id: 'org-positions',   label: 'Cargos',        icon: Briefcase, iconColor: 'text-blue-400',   permission: 'EMPLOYEE_EDIT'  },
          ]
        },
        {
          id: 'attendance-control',
          label: 'Asistencia',
          icon: Calendar,
          iconColor: 'text-orange-500',
          description: 'Gestión de asistencia y tiempo',
          hasSubmenu: true,
          submenu: [
            { id: 'shift-management', label: 'Turnos y Horarios',   icon: Clock,     iconColor: 'text-orange-400', permission: 'ATTENDANCE_EDIT' },
            { id: 'calendar',         label: 'Calendario Laboral',  icon: Calendar,  iconColor: 'text-orange-400', permission: 'CONFIG_EDIT'      },
            { id: 'absences',         label: 'Gestión de Ausencias',icon: CalendarX, iconColor: 'text-red-400',    permission: 'ATTENDANCE_EDIT'  },
            { id: 'vacations',        label: 'Vacaciones',          icon: Umbrella,  iconColor: 'text-teal-400',   permission: 'VACATION_VIEW',   isNew: true }
          ]
        }
      ]
    },
    {
      name: 'Nómina',
      items: [
        {
          id: 'payroll',
          label: 'Nómina',
          icon: DollarSign,
          iconColor: 'text-green-500',
          description: 'Gestión de nómina',
          permission: 'PAYROLL_VIEW',
          hasSubmenu: true,
          submenu: [
            { isGroup: true, id: 'grp-proc',   label: 'Operaciones' },
            { id: 'payroll-main',       label: 'Procesar Nómina',       icon: Calculator,    iconColor: 'text-green-500',  isHighlight: true, permission: 'PAYROLL_PROCESS'  },
            { id: 'variable-incomes',   label: 'Ingresos Variables',    icon: TrendingUp,    iconColor: 'text-emerald-400',                    permission: 'PAYROLL_PROCESS'  },
            { id: 'other-deductions',   label: 'Deducciones',           icon: MinusCircle,   iconColor: 'text-red-400',                        permission: 'PAYROLL_PROCESS'  },
            { id: 'prestamos',          label: 'Préstamos',             icon: CreditCard,    iconColor: 'text-violet-400',                     permission: 'LOAN_VIEW'        },
            { id: 'provisions',         label: 'Provisiones',           icon: Package,       iconColor: 'text-amber-400',                      permission: 'PROVISION_VIEW'   },

            { isGroup: true, id: 'grp-imp',   label: 'TSS e Impuestos' },
            { id: 'social-security',    label: 'Seguridad Social',      icon: Shield,        iconColor: 'text-blue-400',                       permission: 'TSS_VIEW'         },
            { id: 'tss-planillas',      label: 'Planillas TSS',         icon: FileText,      iconColor: 'text-cyan-400',   isNew: true,         permission: 'TSS_VIEW'         },
            { id: 'isr',                label: 'ISR',                   icon: Calculator,    iconColor: 'text-amber-400',                      permission: 'PAYROLL_PROCESS'  },
            { id: 'ir13',               label: 'Conciliación de IR-13', icon: ClipboardList, iconColor: 'text-purple-400',                     permission: 'PAYROLL_PROCESS'  },
            { id: 'creditos-isr',       label: 'Créditos ISR',          icon: CreditCard,    iconColor: 'text-blue-400',                       permission: 'PAYROLL_PROCESS'  },
            { id: 'tax-benefits',       label: 'Beneficios ISR',        icon: Landmark,      iconColor: 'text-emerald-400',                    permission: 'PAYROLL_PROCESS'  },

            { isGroup: true, id: 'grp-anal',  label: 'Análisis y Reportes' },
            { id: 'compare-payrolls',   label: 'Comparativo',           icon: GitCompare,    iconColor: 'text-cyan-400',                       permission: 'PAYROLL_EXPORT'   },
            { id: 'payroll-statistics', label: 'Estadísticas',          icon: BarChart3,     iconColor: 'text-blue-400',                       permission: 'PAYROLL_EXPORT'   },
            { id: 'payroll-reports',    label: 'Reportes',              icon: FileBarChart2, iconColor: 'text-blue-400',                       permission: 'REPORT_VIEW'      },

            { isGroup: true, id: 'grp-cfg',   label: 'Configuración' },
            { id: 'payroll-config',     label: 'Configuración',         icon: Settings,      iconColor: 'text-gray-400',                       permission: 'PAYROLL_CONFIG'   },

            { isGroup: true, id: 'grp-especiales', label: 'Nóminas Especiales' },
            { id: 'bonificaciones',     label: 'Bonificaciones Anuales',icon: Award,         iconColor: 'text-yellow-500', isNew: true,         permission: 'BONUS_VIEW'       },
            { id: 'regalia-pascual',    label: 'Salario de Navidad',    icon: Gift,          iconColor: 'text-red-500',                        permission: 'REGALIAS_VIEW'    },
          ]
        }
      ]
    },
    {
      name: 'Gestión',
      items: [
        {
          id: 'aprobaciones',
          label: 'Aprobaciones',
          icon: CheckSquare,
          iconColor: 'text-amber-500',
          description: 'Pendientes de aprobación',
          permission: 'REQUEST_APPROVE',
          badge: pendientesCount > 0 ? pendientesCount : null,
          badgeNumeric: true
        },
        {
          id: 'prestaciones',
          label: 'Prestaciones',
          icon: Gift,
          iconColor: 'text-pink-500',
          description: 'Beneficios laborales',
          permission: 'PROVISION_VIEW'
        },
        {
          id: 'cafeteria',
          label: 'Cafetería',
          icon: ChefHat,
          iconColor: 'text-orange-500',
          description: 'Menús y selecciones diarias',
          permission: 'CAFETERIA_VIEW'
        },
        {
          id: 'solicitudes',
          label: 'Solicitudes',
          icon: FileText,
          iconColor: 'text-purple-500',
          description: 'Préstamos, vacaciones, certificaciones y más',
          permission: 'REQUEST_VIEW'
        }
      ]
    },
    {
      name: 'Configuración',
      items: [
        {
          id: 'usuarios',
          label: 'Usuarios',
          icon: UserCog,
          iconColor: 'text-slate-500',
          description: 'Gestión de usuarios',
          permission: 'USER_VIEW'
        },
        {
          id: 'roles',
          label: 'Roles',
          icon: Shield,
          iconColor: 'text-indigo-500',
          description: 'Gestión de roles y permisos',
          permission: 'ROLE_VIEW'
        },
        {
          id: 'permisos',
          label: 'Permisos',
          icon: Key,
          iconColor: 'text-indigo-400',
          description: 'Catálogo de permisos del sistema',
          permission: 'ROLE_VIEW'
        },
        {
          id: 'companies',
          label: 'Compañías',
          icon: Briefcase,
          iconColor: 'text-slate-500',
          description: 'Gestión de compañías',
          superAdminOnly: true
        },
        {
          id: 'audit',
          label: 'Auditoría',
          icon: Shield,
          iconColor: 'text-slate-500',
          description: 'Registro de acciones del sistema',
          permission: 'AUDIT_VIEW'
        }
      ]
    }
  ];

  const toggleSidebar = () => setIsOpen(!isOpen);
  const toggleMobileSidebar = () => setIsMobileOpen(!isMobileOpen);

  const toggleSubmenu = (menuId) => {
    setExpandedMenus(prev =>
      prev.includes(menuId) ? prev.filter(id => id !== menuId) : [...prev, menuId]
    );
  };

  // Acordeón: solo un grupo abierto a la vez
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev =>
      prev.includes(groupId) ? prev.filter(id => id !== groupId) : [groupId]
    );
  };

  const handleMenuClick = (item) => {
    if (item.hasSubmenu) {
      toggleSubmenu(item.id);
      if (!expandedMenus.includes(item.id)) {
        const firstVisible = item.submenu.find(s => !s.isGroup && isItemVisible(s));
        if (firstVisible) setCurrentModule(firstVisible.id);
      }
    } else {
      setCurrentModule(item.id);
      if (item.isNew) markAsSeen(item.id);
    }
    if (window.innerWidth < 1024) setIsMobileOpen(false);
  };

  const handleSubmenuClick = (submenuItem) => {
    setCurrentModule(submenuItem.id);
    if (submenuItem.isNew) markAsSeen(submenuItem.id);
    if (window.innerWidth < 1024) setIsMobileOpen(false);
  };

  const isAdminUser = can('REQUEST_APPROVE');

  const isItemVisible = (item) => {
    if (item.superAdminOnly && !currentUser?.is_super_admin) return false;
    if (item.adminOnly && !isAdminUser) return false;
    if (item.permission && !can(item.permission)) return false;
    if (!item.permission && item.roles && !item.roles.includes(currentUser?.role) && !currentUser?.is_super_admin) return false;
    return true;
  };

  // Initials from user name
  const userInitials = currentUser?.username
    ? currentUser.username.slice(0, 2).toUpperCase()
    : 'U';

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={toggleMobileSidebar}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white border border-gray-200 text-gray-600 rounded-lg shadow-sm"
      >
        {isMobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Overlay for mobile */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/30 z-30"
          onClick={toggleMobileSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-screen bg-white border-r border-gray-100 shadow-sm z-40 transition-all duration-300 flex flex-col ${
          isOpen ? 'w-64' : 'w-[72px]'
        } ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        {/* Header — Logo + Collapse */}
        <div className={`flex-shrink-0 flex items-center border-b border-gray-100 h-16 ${isOpen ? 'px-5 justify-between' : 'px-0 justify-center'}`}>
          {isOpen && currentCompany && (
            <button
              onClick={() => setCurrentModule('dashboard')}
              className="flex items-center gap-2 min-w-0 hover:opacity-80 transition-opacity"
              title="Ir al Dashboard"
            >
              {currentCompany.logo ? (
                <img
                  src={getCompanyLogoUrl(currentCompany.logo)}
                  alt={currentCompany.nombre}
                  className="w-8 h-8 object-contain rounded-lg"
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
              ) : null}
              <div className={`w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white text-sm font-bold ${currentCompany.logo ? 'hidden' : ''}`}>
                {currentCompany.nombre?.[0]?.toUpperCase() || '7'}
              </div>
              <span className="text-base font-bold text-gray-800 truncate">
                {currentCompany.nombre}
              </span>
            </button>
          )}

          {!isOpen && currentCompany && (
            <button
              onClick={() => setCurrentModule('dashboard')}
              className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white text-sm font-bold hover:opacity-80 transition-opacity"
              title={currentCompany.nombre}
            >
              {currentCompany.nombre?.[0]?.toUpperCase() || '7'}
            </button>
          )}

          {/* Collapse button — desktop only */}
          <button
            onClick={toggleSidebar}
            className={`hidden lg:flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors flex-shrink-0 ${!isOpen ? 'absolute right-2 top-4' : ''}`}
            title={isOpen ? 'Contraer' : 'Expandir'}
          >
            {isOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3 min-h-0 px-3">
          <div className="space-y-5">
            {menuCategories.map((category) => {
              const visibleItems = category.items.filter(item => {
                if (!isItemVisible(item)) return false;
                if (item.hasSubmenu) {
                  const visibleSubs = item.submenu.filter(s => !s.isGroup && isItemVisible(s));
                  return visibleSubs.length > 0;
                }
                return true;
              });
              if (visibleItems.length === 0) return null;

              return (
                <div key={category.name}>
                  {/* Category label */}
                  {isOpen && (
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-2 mb-1">
                      {category.name}
                    </p>
                  )}

                  <div className="space-y-0.5">
                    {visibleItems.map((item) => {
                      const Icon = item.icon;
                      const isExpanded = expandedMenus.includes(item.id);
                      const isActive = !item.hasSubmenu && currentModule === item.id;
                      const hasActiveSubmenu = item.hasSubmenu && item.submenu.some(sub => !sub.isGroup && sub.id === currentModule);

                      return (
                        <div key={item.id}>
                          {/* Main item */}
                          <button
                            onClick={() => handleMenuClick(item)}
                            title={!isOpen ? item.label : undefined}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all group ${
                              isActive || hasActiveSubmenu
                                ? 'bg-blue-600 text-white shadow-sm shadow-blue-200'
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                            } ${!isOpen ? 'justify-center px-0' : ''}`}
                          >
                            {/* Icon */}
                            <div className="relative flex-shrink-0">
                              <Icon className={`w-[18px] h-[18px] ${
                                isActive || hasActiveSubmenu
                                  ? 'text-white'
                                  : item.iconColor
                              }`} />
                              {/* Dot badge when collapsed */}
                              {!isOpen && item.badgeNumeric && item.badge && (
                                <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                              )}
                            </div>

                            {isOpen && (
                              <>
                                <span className="flex-1 text-left text-sm font-medium truncate">
                                  {item.label}
                                </span>

                                {/* New badge (desaparece al visitar) */}
                                {item.isNew && isNewBadgeVisible(item.id) && (
                                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-600">
                                    Nuevo
                                  </span>
                                )}

                                {/* String badge (Próximamente) */}
                                {item.badge && typeof item.badge === 'string' && (
                                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                                    {item.badge}
                                  </span>
                                )}

                                {/* Numeric badge */}
                                {item.badgeNumeric && item.badge && (
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white min-w-[18px] text-center">
                                    {item.badge}
                                  </span>
                                )}

                                {/* Chevron for submenu */}
                                {item.hasSubmenu && (
                                  isExpanded
                                    ? <ChevronUp className={`w-3.5 h-3.5 flex-shrink-0 ${isActive || hasActiveSubmenu ? 'text-white' : 'text-gray-400'}`} />
                                    : <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 ${isActive || hasActiveSubmenu ? 'text-white' : 'text-gray-400'}`} />
                                )}
                              </>
                            )}
                          </button>

                          {/* Submenu */}
                          {item.hasSubmenu && isExpanded && isOpen && (() => {
                            const hasGroups = item.submenu.some(s => s.isGroup);

                            // ── Renderizado plano (sin grupos) ──────────────────────────
                            if (!hasGroups) return (
                              <div className="ml-3 mt-0.5 mb-1 space-y-0.5 border-l-2 border-gray-100 pl-3">
                                {item.submenu.filter(s => isItemVisible(s)).map((subitem) => {
                                  const SubIcon = subitem.icon;
                                  const isSubActive = currentModule === subitem.id;
                                  return (
                                    <button
                                      key={subitem.id}
                                      onClick={() => handleSubmenuClick(subitem)}
                                      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all text-sm ${
                                        isSubActive
                                          ? 'bg-blue-50 text-blue-600 font-semibold'
                                          : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'
                                      }`}
                                    >
                                      <SubIcon className={`flex-shrink-0 w-3.5 h-3.5 ${isSubActive ? 'text-blue-600' : subitem.iconColor}`} />
                                      <span className="text-xs flex-1 text-left truncate">{subitem.label}</span>
                                      {subitem.isNew && isNewBadgeVisible(subitem.id) && (
                                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-600">
                                          Nuevo
                                        </span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            );

                            // ── Renderizado agrupado (con grupos colapsables) ────────────
                            return (
                              <div className="ml-3 mt-0.5 mb-1 border-l-2 border-gray-100 pl-3">
                                {parseGroups(item.submenu.filter(s => s.isGroup || isItemVisible(s))).filter(g => g.items.length > 0).map((group) => {
                                  const isGroupOpen = expandedGroups.includes(group.id);
                                  const hasActiveItem = group.items.some(s => s.id === currentModule);

                                  return (
                                    <div key={group.id}>
                                      <button
                                        onClick={() => toggleGroup(group.id)}
                                        className={`w-full flex items-center justify-between px-2 pt-2 pb-1 rounded transition-colors hover:bg-gray-50 ${
                                          hasActiveItem && !isGroupOpen ? 'text-blue-500' : 'text-gray-400'
                                        }`}
                                      >
                                        <span className="text-[9px] font-bold uppercase tracking-wider">
                                          {group.label}
                                        </span>
                                        <ChevronRight className={`w-2.5 h-2.5 transition-transform duration-200 ${isGroupOpen ? 'rotate-90' : ''}`} />
                                      </button>

                                      <div style={{
                                        display: 'grid',
                                        gridTemplateRows: isGroupOpen ? '1fr' : '0fr',
                                        transition: 'grid-template-rows 0.2s ease',
                                      }}>
                                        <div style={{ overflow: 'hidden' }}>
                                          {group.items.map((subitem) => {
                                            const SubIcon = subitem.icon;
                                            const isSubActive = currentModule === subitem.id;
                                            const isHighlight = subitem.isHighlight;
                                            return (
                                              <button
                                                key={subitem.id}
                                                onClick={() => handleSubmenuClick(subitem)}
                                                className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg transition-all text-sm ${
                                                  isSubActive
                                                    ? isHighlight
                                                      ? 'bg-green-100 text-green-700 font-semibold border-l-2 border-green-500'
                                                      : 'bg-blue-50 text-blue-600 font-semibold'
                                                    : isHighlight
                                                      ? 'bg-green-50 text-green-700 font-medium hover:bg-green-100 border-l-2 border-green-400'
                                                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'
                                                }`}
                                              >
                                                <SubIcon className={`flex-shrink-0 w-3.5 h-3.5 ${isSubActive ? (isHighlight ? 'text-green-600' : 'text-blue-600') : subitem.iconColor}`} />
                                                <span className="text-xs flex-1 text-left truncate">{subitem.label}</span>
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </nav>

        {/* Footer — User + Logout */}
        <div className={`flex-shrink-0 border-t border-gray-100 p-3 ${!isOpen ? 'px-2' : ''}`}>
          {isOpen ? (
            <div className="bg-gray-50 rounded-xl p-3">
              <div className="flex items-center gap-3 mb-3">
                {/* Avatar */}
                <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                  {userInitials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">
                    {currentUser?.username || 'Usuario'}
                  </p>
                  <p className="text-[10px] text-gray-400 truncate capitalize">
                    {currentUser?.role || 'employee'}
                  </p>
                </div>
                <button
                  onClick={onLogout}
                  className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                  title="Cerrar Sesión"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
              <p className="text-center text-[10px] text-gray-400">
                7Family RRHH <span className="text-blue-500 font-semibold">v4.1.0</span>
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              {/* Avatar collapsed */}
              <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold">
                {userInitials}
              </div>
              <button
                onClick={onLogout}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Cerrar Sesión"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
